import { useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, CalendarPlus, Check, ListChecks, Loader2, ShieldCheck } from "lucide-react";
import { ASSISTANTS, ASSISTANT_API, type ConsentRequest, type ConsentResult } from "@workspace/assistants";
import { BrandLockup } from "@/components/brand-lockup";
import { AccessSwitch, ConnectMarks, INK, MUTED, PURPLE, PillButton, Tag } from "@/components/assistant/assistant-ui";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { useAuth } from "@/hooks/useAuth";
import { useCalendarConnection } from "@/hooks/use-calendar";

/**
 * AI 3: an assistant asking to connect. UnfoldWIN's OAuth server sends the browser here
 * with `?request=<id>`; Allow and Cancel both send it back to the assistant.
 */
export default function AssistantConsentPage() {
  const [, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const requestId = new URLSearchParams(window.location.search).get("request") ?? "";
  const [calendar, setCalendar] = useState(true);
  const [journal, setJournal] = useState(false);
  const [sending, setSending] = useState<"allow" | "cancel" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { connection: calendarConnection } = useCalendarConnection();

  const { data: request, error: loadError, isLoading } = useQuery({
    queryKey: ["/api/oauth/requests", requestId],
    enabled: Boolean(requestId),
    retry: false,
    queryFn: async (): Promise<ConsentRequest> => {
      const res = await authFetch(apiUrl(ASSISTANT_API.consent(requestId)));
      const body = (await res.json().catch(() => ({}))) as ConsentRequest & { error?: string };
      if (!res.ok) throw new Error(body.error ?? "This sign-in request isn't valid any more. Start again from your assistant.");
      return body;
    },
  });

  // The backend says whether a calendar is connected; the calendar layer also knows when
  // Google has ended that connection, and then the permission waits for a reconnect.
  const calendarEnded = calendarConnection?.status === "needs_reconnect";
  const calendarReady = Boolean(request?.calendarConnected) && !calendarEnded;

  async function decide(kind: "allow" | "cancel") {
    setSending(kind);
    setError(null);
    try {
      const path = kind === "allow" ? ASSISTANT_API.approve(requestId) : ASSISTANT_API.deny(requestId);
      const res = await authFetch(apiUrl(path), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: kind === "allow" ? JSON.stringify({ calendar: calendar && calendarReady, journal }) : undefined,
      });
      const body = (await res.json().catch(() => ({}))) as Partial<ConsentResult> & { error?: string };
      if (!res.ok || !body.redirectTo) throw new Error(body.error ?? "Something went wrong. Try again from your assistant.");
      window.location.assign(body.redirectTo);
    } catch (err) {
      setError((err as Error).message);
      setSending(null);
    }
  }

  let card: ReactNode;
  if (!requestId || loadError) {
    card = (
      <Message title="This link has expired" body={(loadError as Error | null)?.message ?? "Start connecting again from your assistant."}>
        <PillButton variant="outline" className="w-full" onClick={() => setLocation("/")}>
          Go to UnfoldWIN
        </PillButton>
      </Message>
    );
  } else if (isLoading || !request) {
    card = (
      <div className="flex justify-center py-16">
        <Loader2 className={`size-6 animate-spin ${PURPLE}`} />
      </div>
    );
  } else if (!request.assistant) {
    card = (
      <Message
        title={`${request.clientName} can't connect yet`}
        body="Right now UnfoldWIN works with Claude and ChatGPT. Your Next Steps stay private to you."
      >
        <PillButton variant="outline" className="w-full" disabled={sending !== null} onClick={() => void decide("cancel")}>
          Go back
        </PillButton>
      </Message>
    );
  } else if (!request.included) {
    const name = ASSISTANTS[request.assistant].name;
    card = (
      <>
        <ConnectMarks assistant={request.assistant} assistantFirst size={44} />
        <h1 className={`text-2xl font-semibold leading-[30px] tracking-[-0.04em] ${INK}`}>Connect {name} with Plus or Premium</h1>
        <p className={`text-sm leading-relaxed ${MUTED}`}>
          AI assistants are part of UnfoldWIN Plus and Premium. Upgrade, then connect {name} again from its settings.
        </p>
        <div className="flex gap-3">
          <PillButton variant="outline" className="flex-1" disabled={sending !== null} onClick={() => void decide("cancel")}>
            Cancel
          </PillButton>
          <PillButton className="flex-1" onClick={() => setLocation("/pricing")}>
            See plans
          </PillButton>
        </div>
      </>
    );
  } else {
    const name = ASSISTANTS[request.assistant].name;
    card = (
      <>
        <ConnectMarks assistant={request.assistant} assistantFirst size={44} />
        <h1 className={`text-2xl font-semibold leading-[30px] tracking-[-0.04em] ${INK}`}>{name} wants to connect to your UnfoldWIN</h1>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
          <span className={MUTED}>Signed in as</span>
          <span className={`font-medium ${INK} break-all`}>{user?.email}</span>
          <button type="button" onClick={() => void logout()} className={`font-medium ${PURPLE} hover:underline`}>
            Not you?
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[rgba(47,41,37,0.1)]">
          <Permission icon={<ListChecks />} title="See your open Next Steps" detail="Step text, month, source title and scheduled time." control={<Tag>Required</Tag>} />
          <Permission icon={<Check />} title="Tick steps done and add notes" detail={`${name} asks you before each action.`} control={<Tag>Required</Tag>} />
          <Permission
            icon={<CalendarPlus />}
            title="Add steps to your calendar"
            detail={
              calendarEnded
                ? "Reconnect Google Calendar in UnfoldWIN first."
                : calendarReady
                  ? "Uses your connected Google Calendar."
                  : "Connect Google Calendar in UnfoldWIN first."
            }
            control={
              <AccessSwitch
                checked={calendar && calendarReady}
                disabled={!calendarReady}
                onCheckedChange={setCalendar}
                label="Add steps to your calendar"
              />
            }
          />
          <Permission
            icon={<BookOpen />}
            title="Read your journal entries"
            detail={journal ? "Summaries of this month's Unfoldings, for 24 hours." : "The reflections behind your steps."}
            control={<AccessSwitch checked={journal} onCheckedChange={setJournal} label="Read your journal entries" />}
            last
          />
        </div>

        <div className="flex gap-[10px]">
          <ShieldCheck className="mt-px size-[15px] shrink-0 text-[#6e53ac]" />
          <p className={`text-xs leading-[18px] ${MUTED}`}>
            You can change this or disconnect {name} anytime in Settings → AI assistants. {name} never sees your password.
          </p>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <div className="flex gap-3">
          <PillButton variant="outline" className="flex-1" disabled={sending !== null} onClick={() => void decide("cancel")}>
            Cancel
          </PillButton>
          <PillButton className="flex-1" disabled={sending !== null} onClick={() => void decide("allow")}>
            {sending === "allow" ? <Loader2 className="size-4 animate-spin" /> : null}
            Allow
          </PillButton>
        </div>
      </>
    );
  }

  return (
    <div className="relative flex min-h-dvh flex-col bg-[hsl(40_30%_97%)]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(201,188,245,0.35), transparent 70%)" }}
      />
      <header className="relative px-6 py-6 sm:px-16">
        <BrandLockup />
      </header>
      <main className="relative flex flex-1 items-start justify-center px-4 pb-10 pt-4 sm:items-center sm:pt-0">
        <div className="flex w-full max-w-[520px] flex-col gap-[18px] rounded-[28px] border border-[rgba(47,41,37,0.08)] bg-white p-6 shadow-[0_24px_28px_rgba(47,41,37,0.12),0_1px_1px_rgba(47,41,37,0.06)] sm:p-9">
          {card}
        </div>
      </main>
      <footer className={`relative pb-6 text-center text-[13px] ${MUTED}`}>unfoldwin.com · Connecting an AI assistant</footer>
    </div>
  );
}

function Permission({
  icon,
  title,
  detail,
  control,
  last = false,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  control: ReactNode;
  last?: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${last ? "" : "border-b border-[rgba(47,41,37,0.08)]"}`}>
      <span className={`shrink-0 [&>svg]:size-[17px] ${INK}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${INK}`}>{title}</p>
        <p className={`mt-0.5 text-xs leading-[17px] ${MUTED}`}>{detail}</p>
      </div>
      {control}
    </div>
  );
}

function Message({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <>
      <h1 className={`text-2xl font-semibold leading-[30px] tracking-[-0.04em] ${INK}`}>{title}</h1>
      <p className={`text-sm leading-relaxed ${MUTED}`}>{body}</p>
      {children}
    </>
  );
}
